import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { useFetch } from '@rad/hooks';
import { withDone } from '@rad/utility/http';
import { onSubdomain, districtRoutesArray, adminRoutesArray, generalRoutes, generalRoutesArray, propertyRoutesArray, orgRoutesArray, systemRoutesArray, accountRoutes, accountRoutesArray, exploreRoutesArray, paymentRoutesArray, getBaseURL, hasAnyAdminPermission, } from './common/routes';
import { routeIsRender } from './_common';
import { colorKeys, coreFromSettings } from './theme/config';
import { setCommunityTheme } from './theme/root';
import { DistrictProvider } from './hooks/useDistrict';
import { getDistrict, getThemeSettings } from './common/network/district';
import { getActiveBanners } from './common/network/banner';
import { ToastProvider } from './contexts/toast';
import E404 from './screens/system/404';
import Admin404 from './screens/system/admin-404';
import AppCommon from './AppCommon';
import { communityPath, withoutCommunityPath } from './common/community-path';
// Explore, claim, and pricing are registered only by the root-domain app, so a district hands
// them back to the root host with the path and query intact.
const rootDomainRoutesArray = [...exploreRoutesArray, ...paymentRoutesArray];
const rootDomainURL = () => `${getBaseURL()}${withoutCommunityPath(window.location.pathname)}${window.location.search}`;
const routingConfig = {
    'routeSets': [
        {
            'name': 'general',
            'available': () => generalRoutesArray,
            'description': 'Always available (login, signup, etc.)',
            'onInvalidAccess': [],
        },
        {
            'name': 'account',
            'available': () => accountRoutesArray,
            'description': 'User-account pages (account, profile, membership, owner property management) — available on both root and tenant',
            'onInvalidAccess': [],
        },
        {
            'name': 'district',
            'available': (ctx) => ctx.onSubdomain ? districtRoutesArray : [],
            'description': 'District pages (only on subdomains)',
            'onInvalidAccess': [
                {
                    name: 'district-on-root-domain',
                    when: (ctx) => !ctx.onSubdomain && !ctx.matched && districtRoutesArray.some(r => ctx.currentPath[0] === r.path[0]),
                    action: 'redirect',
                    target: () => generalRoutes.login.getURL(),
                    description: 'Redirect district routes accessed on root domain'
                }
            ],
        },
        {
            'name': 'root-domain',
            'available': () => [],
            'description': 'Explore, claim, and payment pages (only on the root domain)',
            'onInvalidAccess': [
                {
                    name: 'root-domain-route-on-district',
                    when: (ctx) => !ctx.matched
                        && (getBaseURL() !== window.location.origin || communityPath !== '')
                        && rootDomainRoutesArray.some(r => ctx.currentPath[0] === r.path[0]),
                    action: 'redirect',
                    target: rootDomainURL,
                    description: 'Send root-domain-only routes reached on a district host to the root host'
                }
            ],
        },
        {
            'name': 'property',
            'available': () => [...propertyRoutesArray, ...orgRoutesArray],
            'description': 'Property pages',
            'onInvalidAccess': [],
        },
        {
            'name': 'admin',
            // Gate the entire admin route set on holding at least one admin
            // permission. The hub routes (`manage`, `propertiesHub`, etc.) have
            // empty permission lists so any admin can land on a shell that
            // shows what they can access — but without ANY admin permission
            // those hubs are dead-end shells. Skip registering the routes for
            // non-admins entirely; the 'admin-no-permissions' redirect below
            // catches their URL visits and sends them to /account.
            'available': (ctx) => {
                if (!ctx.isAuthenticated)
                    return [];
                if (!hasAnyAdminPermission([adminRoutesArray, systemRoutesArray], ctx.hasPermission))
                    return [];
                return adminRoutesArray.filter(r => r.permissions.length === 0 || ctx.hasPermission(r.permissions));
            },
            'description': 'Admin panel (requires auth + at least one admin permission)',
            'onInvalidAccess': [
                {
                    name: 'protected-without-auth',
                    when: (ctx) => !ctx.matched && !ctx.isAuthenticated && adminRoutesArray.some(r => ctx.currentPath[0] === r.path[0]),
                    action: 'redirect',
                    target: () => generalRoutes.login.getURL(),
                    description: 'Redirect protected admin routes to login'
                },
                {
                    name: 'admin-no-permissions',
                    when: (ctx) => !ctx.matched && ctx.isAuthenticated && ctx.currentPath[0] === 'manage' &&
                        !hasAnyAdminPermission([adminRoutesArray, systemRoutesArray], ctx.hasPermission),
                    action: 'redirect',
                    target: () => accountRoutes.accountDefault.getURL(),
                    description: 'Send authenticated non-admin users from /manage to /account'
                },
                {
                    name: 'admin-not-found',
                    when: (ctx) => !ctx.matched && ctx.isAuthenticated && ctx.currentPath[0] === 'manage',
                    action: 'render',
                    component: Admin404,
                    description: 'Show admin 404 for authenticated admin users hitting an unknown /manage URL'
                }
            ],
        },
        {
            'name': 'system',
            'available': (ctx) => {
                return (ctx.isAuthenticated
                    ? systemRoutesArray.filter(r => r.permissions.length === 0 || ctx.hasPermission(r.permissions))
                    : []);
            },
            'description': 'System admin (requires auth + system permissions)',
            'onInvalidAccess': [
                {
                    name: 'system-without-auth',
                    when: (ctx) => !ctx.matched && !ctx.isAuthenticated && ctx.currentPath[0] === 'system',
                    action: 'redirect',
                    target: () => generalRoutes.login.getURL(),
                    description: 'Redirect system routes to login'
                }
            ],
        }
    ],
    defaultFallback: {
        name: 'default-not-found',
        when: (ctx) => !ctx.matched,
        action: 'render',
        component: E404,
        description: 'Default 404 for all unmatched routes'
    },
    /**
     * Error Handlers - Explicit handlers for HTTP errors and route failures
     * Must be explicitly defined even if they delegate to helper functions
     */
    on401: () => {
        localStorage.removeItem('userToken');
        window.location.href = generalRoutes.login.getURL();
    },
    on404: (ctx) => {
        var _a, _b;
        return (_b = (_a = routingConfig
            .routeSets
            .flatMap(set => set.onInvalidAccess)
            .filter(h => h.when(ctx))
            .find(routeIsRender)) === null || _a === void 0 ? void 0 : _a.component) !== null && _b !== void 0 ? _b : routingConfig.defaultFallback.component;
    },
};
const App = () => {
    const [districtPromise, reloadDistrict] = useFetch(getDistrict);
    const [displaySettings, setDisplaySettings] = React.useState({});
    const [banners, setBanners] = React.useState([]);
    React.useEffect(() => {
        // These calls require a subdomain (tenant resolver); skip on root domain
        if (!onSubdomain)
            return;
        reloadDistrict();
        // Load banners once on mount
        withDone(getActiveBanners())().then((result) => {
            if (result)
                setBanners(result);
        });
        // Load district theme settings from DB: the colors dress the page, the rest are display settings
        withDone(getThemeSettings())().then((settings) => {
            if (settings && settings.length > 0) {
                setDisplaySettings(Object.fromEntries(settings.filter(s => !colorKeys.has(s.key)).map(s => [s.key, s.value])));
                setCommunityTheme(coreFromSettings(settings));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (_jsx(AppCommon, { routingConfig: routingConfig, wrapper: (children) => _jsx(_Fragment, { children: _jsx(DistrictProvider, { districtPromise: districtPromise, displaySettings: displaySettings, banners: banners, children: _jsx(ToastProvider, { children: children }) }) }) }));
};
export default App;
